import React from 'react';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { buildApiUrl } from '../config/environment';
import EmojiGif from './EmojiGif';
import { getTodayOpeningHours, formatTodayHours, getOpenStatusClasses, getOpenStatusText } from '../utils/openingHours';

interface MapLegendProps {
  nearbyRestaurants: any[];
  selectedCharger: any;
  chargerAvailability: any;
  onRestaurantClick?: (restaurant: any) => void;
  selectedRestaurantIndex: number | null;
  setSelectedRestaurantIndex: (index: number | null) => void;
  t: (key: any) => string;
  routingInfo?: Map<string, any>; // Navigation routing information
  onReportNonRestaurant?: (restaurant: any) => void;
  userPreferences?: {
    preferred_chains: string[];
    keywords: string[];
    search_level: string;
  } | null; // User's restaurant preferences
}

export const MapLegend: React.FC<MapLegendProps> = ({
  nearbyRestaurants,
  selectedCharger,
  chargerAvailability,
  onRestaurantClick,
  selectedRestaurantIndex,
  setSelectedRestaurantIndex,
  t,
  routingInfo,
  onReportNonRestaurant,
  userPreferences
}) => {
  const { isMobile, isTablet, isDesktop } = useResponsiveLayout();

  // Debug logging for MapLegend props
  console.log('🔧 MapLegend render - nearbyRestaurants:', nearbyRestaurants?.length || 0);
  console.log('🔧 MapLegend render - selectedCharger:', selectedCharger);
  console.log('🔧 MapLegend render - chargerAvailability:', chargerAvailability);

  // Helper to get restaurant photo URL from API cache
  const getRestaurantPhotoUrl = (restaurant: any) => {
    if (!restaurant.photos || restaurant.photos.length === 0) return null;

    return buildApiUrl(
      `api/photos/${restaurant.photos[0].photo_reference}?maxwidth=300&placeId=${restaurant.place_id || ''}&restaurantName=${encodeURIComponent(restaurant.name)}`
    );
  };

  // Check if a restaurant matches user preferences
  const isRestaurantFavorite = (restaurant: any): boolean => {
    if (!userPreferences) return false;

    const restaurantName = restaurant.name?.toLowerCase() || '';
    const cuisineTypes = restaurant.cuisine_types || [];

    // Normalize text by removing special characters and spaces for better matching
    const normalizeText = (text: string) => text.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedRestaurantName = normalizeText(restaurantName);

    // Check preferred chains with normalized matching
    const matchesChain = userPreferences.preferred_chains.some(chain => {
      const normalizedChain = normalizeText(chain);
      return normalizedRestaurantName.includes(normalizedChain);
    });

    // Check keywords in restaurant name or cuisine types
    const matchesKeyword = userPreferences.keywords.some(keyword => {
      const keywordLower = keyword.toLowerCase();
      return restaurantName.includes(keywordLower) ||
             cuisineTypes.some((cuisine: string) => cuisine.toLowerCase().includes(keywordLower));
    });

    return matchesChain || matchesKeyword;
  };

  return (
    <div className={isMobile ? "space-y-2" : "space-y-4"}>
      {/* Supercharger info now shown in header for all viewports */}

      {/* Restaurants Section - Grouped by Supercharger */}
      {nearbyRestaurants.length > 0 && (
        <div>          
          <div className={`space-y-3 ${isMobile ? 'space-y-2' : 'space-y-3'} overflow-y-auto`}>
            {(() => {
              // Group restaurants by supercharger
              const restaurantsByCharger = new Map();
              nearbyRestaurants.forEach((restaurant, index) => {
                // Use selectedCharger as fallback if restaurant.nearCharger is missing
                const chargerData = restaurant.nearCharger || selectedCharger;
                
                // Skip restaurants without any charger data
                if (!chargerData || !chargerData.openchargeId) {
                  return;
                }
                
                const chargerId = chargerData.openchargeId;
                
                if (!restaurantsByCharger.has(chargerId)) {
                  restaurantsByCharger.set(chargerId, []);
                }
                restaurantsByCharger.get(chargerId).push({...restaurant, originalIndex: index});
              });

              // Render each supercharger group
              return Array.from(restaurantsByCharger.entries()).map(([chargerId, restaurants]) => (
                <div key={chargerId}>
                  {/* Supercharger heading with routing info */}
                  <div className={`${isMobile ? 'mb-1' : 'mb-2'}`}>
                    <h5 className={`font-semibold text-gray-800 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                      {(restaurants[0].nearCharger || selectedCharger)?.name}
                      {(() => {
                        // Calculate stall count directly from charger data
                        const charger = restaurants[0].nearCharger || selectedCharger;
                        if (!charger) return null;
                        
                        // Debug logging for stall count data
                        console.log('🔧 Stall count debug - charger object:', charger);
                        console.log('🔧 numberOfPoints:', charger.numberOfPoints);
                        console.log('🔧 numberOfChargingPoints:', charger.numberOfChargingPoints);
                        console.log('🔧 connections:', charger.connections);
                        
                        // ONLY use numberOfPoints from OpenChargeMap API - NO FALLBACKS
                        let stallCount = null;

                        if (charger.numberOfPoints && charger.numberOfPoints > 0) {
                          stallCount = charger.numberOfPoints;
                          console.log('🔧 ✅ Using OpenChargeMap numberOfPoints:', stallCount);
                        } else {
                          console.log('🔧 ❌ No valid numberOfPoints from OpenChargeMap API');
                          stallCount = null;
                        }

                        return stallCount !== null ? (
                          <span className={`ml-2 text-gray-600 font-normal ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                            ⚡ {stallCount} {t('legend_stalls')}
                          </span>
                        ) : (
                          <span className={`ml-2 text-gray-500 font-normal ${isMobile ? 'text-[10px]' : 'text-xs'}`}>
                            ⚡ {t('legend_naStalls')}
                          </span>
                        );
                      })()}
                    </h5>
                    {routingInfo && routingInfo.get(chargerId) && (
                      <div className={`text-gray-600 ${isMobile ? 'text-[10px]' : 'text-xs'} mt-1`}>
                        🧭 {routingInfo.get(chargerId).distanceKm}km {t('legend_fromYourLocation')} •
                        ⏱️ {routingInfo.get(chargerId).durationFormatted || `${routingInfo.get(chargerId).durationMinutes}min`} {t('legend_drive')} •
                        🕐 {t('legend_arriveAt')} {routingInfo.get(chargerId).arrivalTimeFormatted}
                      </div>
                    )}
                  </div>
                  
                  {/* Restaurants for this supercharger */}
                  <div className={`space-y-2 ${isMobile ? 'space-y-1' : 'space-y-2'} ml-2`}>
                    {(() => {
                      // Sort restaurants: favorites first (by rating), then non-favorites (by rating)
                      const sortedRestaurants = [...restaurants].sort((a, b) => {
                        const aIsFavorite = isRestaurantFavorite(a);
                        const bIsFavorite = isRestaurantFavorite(b);

                        // If both are favorites or both are not favorites, sort by rating
                        if (aIsFavorite === bIsFavorite) {
                          const aRating = a.rating || 0;
                          const bRating = b.rating || 0;
                          return bRating - aRating; // Higher rating first
                        }

                        // Favorites come first
                        return bIsFavorite ? 1 : -1;
                      });

                      return sortedRestaurants.map((restaurant: any) => {
                      const index = restaurant.originalIndex;
                      // Get proper opening hours data (never assumes open/closed without valid data)
                      const openingHoursData = getTodayOpeningHours(restaurant.opening_hours);
                      return (
              <div
                key={restaurant.id}
                className={`
                  restaurant-card border rounded-lg cursor-pointer transition-all
                  ${selectedRestaurantIndex === index 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }
                  ${isMobile ? 'p-2 text-xs' : 'p-3 text-sm'}
                `}
                onClick={() => {
                  if (selectedRestaurantIndex === index) {
                    setSelectedRestaurantIndex(null);
                  } else {
                    setSelectedRestaurantIndex(index);
                    onRestaurantClick?.(restaurant);
                  }
                }}
              >
                {/* Row 1: Map marker + Restaurant name (truncated at 30 chars) */}
                <div className={`flex items-center ${isMobile ? 'space-x-1' : 'space-x-2'} mb-1`}>
                  <span 
                    className={`
                      text-white font-bold rounded-full flex items-center justify-center flex-shrink-0
                      ${isMobile ? 'text-[10px] w-4 h-4' : 'text-xs w-6 h-6'}
                    `}
                    style={{ 
                      backgroundColor: restaurant.networkColor || '#3b82f6' // Use network color or default to blue
                    }}
                  >
                    {String.fromCharCode(65 + index)}
                  </span>
                  <div className="flex items-center flex-1 min-w-0">
                    <h5 className={`font-medium flex-1 min-w-0 ${isMobile ? 'text-xs' : 'text-sm'}`} 
                        style={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '30ch' // Truncate at 30 characters
                        }}>
                      {restaurant.name}
                    </h5>
                    {isRestaurantFavorite(restaurant) && (
                      <span
                        className={`ml-1 flex-shrink-0 ${isMobile ? 'text-xs' : 'text-sm'}`}
                        title={t('legend_favoriteRestaurant')}
                      >
                        ❤️
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Row 2: Distance + Rating + Price level */}
                <div className={`flex items-center ${isMobile ? 'space-x-1 text-[10px]' : 'space-x-2 text-sm'} text-gray-600`}>
                  {/* Distance */}
                  <span className="flex-shrink-0">
                    {restaurant.distance_km
                      ? `${Math.round(restaurant.distance_km * 1000)} m`
                      : restaurant.distance
                        ? `${Math.round(restaurant.distance * 1000)} m`
                        : '20m'
                    }
                  </span>

                  {/* Rating */}
                  {restaurant.rating && (
                    <div className="flex items-center flex-shrink-0">
                      <EmojiGif emoji="⭐" size={isMobile ? "sm" : "md"} className="mr-0.5" />
                      <span>{restaurant.rating.toFixed(1)}</span>
                    </div>
                  )}

                  {/* Price level - Google Maps values: null/0/1=1 icon, 2=2 icons, 3=3 icons, 4=4 icons */}
                  <div className="flex items-center flex-shrink-0">
                    {(() => {
                      // Handle Google price_level: null, undefined, 0, or 1 → show 1 icon
                      const priceLevel = restaurant.price_level;
                      const iconCount = (priceLevel === null || priceLevel === undefined || priceLevel === 0 || priceLevel === 1) ? 1 : priceLevel;

                      return Array.from({ length: Math.min(iconCount, 4) }).map((_, i) => (
                        <EmojiGif key={i} emoji="💰" size={isMobile ? "sm" : "md"} />
                      ));
                    })()}
                  </div>
                </div>

                {/* Row 3: Opening hours and status - Always visible */}
                <div className={`flex items-center justify-between ${isMobile ? 'text-[10px] mt-1' : 'text-xs mt-2'}`}>
                  {openingHoursData.hasValidData ? (
                    <>
                      <span className="text-gray-600 flex-1 truncate">
                        {formatTodayHours(openingHoursData.todayHours)}
                      </span>
                      {openingHoursData.isCurrentlyOpen !== null && (
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs flex-shrink-0 ${getOpenStatusClasses(openingHoursData.isCurrentlyOpen)}`}>
                          {getOpenStatusText(openingHoursData.isCurrentlyOpen)}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-500 px-2 py-0.5 rounded bg-gray-100">
                      {t('legend_hoursNotAvailable')}
                    </span>
                  )}
                </div>
                
                {/* Expanded content for selected restaurant */}
                {selectedRestaurantIndex === index && (
                  <div className={`${isMobile ? 'mt-2 pt-2' : 'mt-3 pt-3'} border-t border-gray-200`}>
                    {/* Restaurant Photo - smaller on mobile */}
                    {restaurant.photos && restaurant.photos.length > 0 && getRestaurantPhotoUrl(restaurant) && (
                      <div className={`${isMobile ? 'mb-2' : 'mb-3'}`}>
                        <img 
                          src={getRestaurantPhotoUrl(restaurant)!}
                          alt={restaurant.name}
                          className={`w-full rounded-md object-cover ${isMobile ? 'h-16' : 'h-32'}`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Restaurant Details - more compact */}
                    <div className={`space-y-1 ${isMobile ? 'text-[10px]' : 'text-sm'}`}>
                      <div className="flex items-center">
                        {(() => {
                          // Handle Google price_level: null, undefined, 0, or 1 → show 1 icon
                          const priceLevel = restaurant.price_level;
                          const iconCount = (priceLevel === null || priceLevel === undefined || priceLevel === 0 || priceLevel === 1) ? 1 : priceLevel;
                          
                          return Array.from({ length: Math.min(iconCount, 4) }).map((_, i) => (
                            <EmojiGif key={i} emoji="💰" size={isMobile ? "sm" : "md"} className="mr-0.5" />
                          ));
                        })()}
                        <span className="text-gray-600 ml-1">
                          {restaurant.price_level === 1 ? t('legend_priceBudget') :
                           restaurant.price_level === 2 ? t('legend_priceModerate') :
                           restaurant.price_level === 3 ? t('legend_priceExpensive') :
                           restaurant.price_level === 4 ? t('legend_priceVeryExpensive') : t('legend_priceBudget')}
                        </span>
                      </div>

                      {restaurant.address && (
                        <p className="text-gray-600 truncate">{restaurant.address}</p>
                      )}

                      {restaurant.user_ratings_total && (
                        <div className="text-gray-600">
                          {restaurant.user_ratings_total} {t('legend_reviews')}
                        </div>
                      )}
                    </div>

                    <div className={`flex flex-wrap gap-1 ${isMobile ? 'mt-2' : 'mt-3'}`}>
                      {restaurant.website && (
                        <button
                          className={`text-blue-600 hover:text-blue-800 underline ${isMobile ? 'text-[10px]' : 'text-sm'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(restaurant.website, '_blank');
                          }}
                        >
                          {t('legend_web')}
                        </button>
                      )}
                      
                      {restaurant.formatted_phone_number && (
                        <button
                          className={`text-blue-600 hover:text-blue-800 underline ${isMobile ? 'text-[10px]' : 'text-sm'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`tel:${restaurant.formatted_phone_number}`, '_blank');
                          }}
                        >
                          {t('legend_call')}
                        </button>
                      )}

                      <button
                        className={`text-blue-600 hover:text-blue-800 underline ${isMobile ? 'text-[10px]' : 'text-sm'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Generate walking directions from charger to restaurant
                          const chargerCoords = `${selectedCharger?.latitude},${selectedCharger?.longitude}`;
                          const restaurantCoords = `${restaurant.geometry?.location?.lat},${restaurant.geometry?.location?.lng}`;
                          const directionsUrl = `https://www.google.com/maps/dir/${chargerCoords}/${restaurantCoords}/`;
                          window.open(directionsUrl, '_blank');
                        }}
                      >
                        {t('legend_directions')}
                      </button>

                      <button
                        className={`text-red-600 hover:text-red-800 underline ${isMobile ? 'text-[10px]' : 'text-sm'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onReportNonRestaurant?.(restaurant);
                        }}
                        title={t('legend_reportTooltip')}
                      >
                        {t('legend_notARestaurant')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
                      );
                      });
                    })()}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* Layout Debug Info (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="text-xs text-gray-500 mt-4 p-2 bg-gray-100 rounded">
          Layout: {isMobile ? 'Mobile (Bottom)' : isTablet ? 'Tablet (Left)' : 'Desktop (Right)'}
        </div>
      )}
    </div>
  );
};